import api from './api';

export type WarehouseType = 'main' | 'branch' | 'transit' | 'virtual';

export interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: WarehouseType;
  address: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  contactPerson: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  description: string | null;
  isActive: boolean;
  // Stock goes here when a document names no warehouse
  isDefault: boolean;
  // Different products with stock here, and the units and value they add up to
  productCount?: number;
  totalQuantity?: number;
  stockValue?: number;
  createdAt: string;
  updatedAt: string;
}

export interface WarehouseInput {
  code: string;
  name: string;
  type?: WarehouseType;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  contactPerson?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  description?: string | null;
  isActive?: boolean;
}

export interface Page<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface WarehouseQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}

export interface WarehouseSummary {
  total: number;
  active: number;
  inactive: number;
  defaultWarehouse: { id: string; code: string; name: string } | null;
  withStock: number;
}

export interface WarehouseStockRow {
  productId: string;
  sku: string;
  name: string;
  unit: string;
  // In this warehouse, and in all warehouses together
  quantity: number;
  totalStock: number;
  costPrice: number;
  value: number;
}

export interface ProductLocation {
  warehouseId: string;
  code: string;
  name: string;
  isDefault: boolean;
  quantity: number;
}

export type StockTransferStatus = 'draft' | 'pending' | 'in_transit' | 'received' | 'cancelled';

export interface StockTransferItem {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  requestedQuantity: number;
  shippedQuantity: number;
  receivedQuantity: number;
  unitCost: number | null;
  notes: string | null;
  // What the source warehouse holds right now
  availableInSource: number;
}

export interface StockTransfer {
  id: string;
  transferNumber: string;
  fromWarehouseId: string;
  toWarehouseId: string;
  fromWarehouse: Warehouse | null;
  toWarehouse: Warehouse | null;
  status: StockTransferStatus;
  transferDate: string;
  expectedReceiptDate: string | null;
  actualReceiptDate: string | null;
  shippingMethod: string | null;
  trackingNumber: string | null;
  notes: string | null;
  itemCount: number;
  totalQuantity: number;
  items?: StockTransferItem[];
  createdAt: string;
  updatedAt: string;
}

export interface StockTransferInput {
  fromWarehouseId: string;
  toWarehouseId: string;
  transferDate?: string;
  expectedReceiptDate?: string | null;
  shippingMethod?: string | null;
  trackingNumber?: string | null;
  notes?: string | null;
  items: { productId: string; quantity: number; notes?: string | null }[];
}

export interface StockTransferQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: StockTransferStatus | '';
  warehouseId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface StockTransferSummary {
  total: number;
  draft: number;
  inTransit: number;
  received: number;
  cancelled: number;
  unitsInTransit: number;
}

export interface ReceiveInput {
  items?: { itemId: string; receivedQuantity: number }[];
  notes?: string;
}

// Empty filters are left out of the request
const clean = (params: object) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

export const warehouseService = {
  getPage: async (params: WarehouseQueryParams): Promise<Page<Warehouse>> => (await api.get('/warehouses', { params: clean(params) })).data,
  // The warehouses a document can name, default first
  getActive: async (): Promise<Warehouse[]> => (await api.get('/warehouses/active')).data,
  getSummary: async (): Promise<WarehouseSummary> => (await api.get('/warehouses/summary')).data,
  getStock: async (id: string, params: { page?: number; limit?: number; search?: string }): Promise<Page<WarehouseStockRow>> =>
    (await api.get(`/warehouses/${id}/stock`, { params: clean(params) })).data,
  getProductLocations: async (productId: string): Promise<ProductLocation[]> => (await api.get(`/inventory/${productId}/locations`)).data,
  create: async (data: WarehouseInput): Promise<Warehouse> => (await api.post('/warehouses', data)).data,
  update: async (id: string, data: Partial<WarehouseInput>): Promise<Warehouse> => (await api.patch(`/warehouses/${id}`, data)).data,
  setDefault: async (id: string): Promise<Warehouse> => (await api.post(`/warehouses/${id}/default`)).data,
  remove: async (id: string): Promise<void> => {
    await api.delete(`/warehouses/${id}`);
  },
};

export const stockTransferService = {
  getPage: async (params: StockTransferQueryParams): Promise<Page<StockTransfer>> =>
    (await api.get('/stock-transfers', { params: clean(params) })).data,
  getSummary: async (): Promise<StockTransferSummary> => (await api.get('/stock-transfers/summary')).data,
  getOne: async (id: string): Promise<StockTransfer> => (await api.get(`/stock-transfers/${id}`)).data,
  create: async (data: StockTransferInput): Promise<StockTransfer> => (await api.post('/stock-transfers', data)).data,
  update: async (id: string, data: Partial<StockTransferInput>): Promise<StockTransfer> => (await api.patch(`/stock-transfers/${id}`, data)).data,
  ship: async (id: string): Promise<StockTransfer> => (await api.post(`/stock-transfers/${id}/ship`)).data,
  receive: async (id: string, data: ReceiveInput): Promise<StockTransfer> => (await api.post(`/stock-transfers/${id}/receive`, data)).data,
  cancel: async (id: string): Promise<StockTransfer> => (await api.post(`/stock-transfers/${id}/cancel`)).data,
  remove: async (id: string): Promise<void> => {
    await api.delete(`/stock-transfers/${id}`);
  },
};

// ---- Goods receipts (GRN)

export interface GoodsReceiptLine {
  id: string;
  purchaseOrderItemId: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  orderedQuantity: number;
  receivedQuantity: number;
  acceptedQuantity: number;
  rejectedQuantity: number;
  rejectionReason: string | null;
  unitCost: number;
  batchNumber: string | null;
  heatNumber: string | null;
  millTcNumber: string | null;
  notes: string | null;
  // Set when the line was held for incoming inspection
  inspectionLotNumber?: string | null;
  inspectionStatus?: string | null;
}

export interface GoodsReceipt {
  id: string;
  grnNumber: string;
  receiptDate: string;
  status: 'posted' | 'cancelled';
  purchaseOrderId: string;
  purchaseOrderNumber: string;
  vendorName: string | null;
  warehouseId: string;
  warehouseName: string | null;
  vendorChallanNumber: string | null;
  vendorInvoiceNumber: string | null;
  vehicleNumber?: string | null;
  notes?: string | null;
  cancelReason?: string | null;
  itemCount: number;
  receivedQuantity: number;
  acceptedQuantity: number;
  rejectedQuantity: number;
  acceptedValue: number;
  items?: GoodsReceiptLine[];
  createdAt: string;
}

export interface GoodsReceiptSummary {
  posted: number;
  cancelled: number;
  thisMonth: number;
  rejectedUnits: number;
  ordersAwaitingReceipt: number;
  unitsAwaitingReceipt: number;
}

export interface ReceivableOrder {
  id: string;
  orderNumber: string;
  status: string;
  expectedDate: string;
  vendorName: string | null;
  orderedQuantity: number;
  receivedQuantity: number;
  pendingQuantity: number;
}

export interface PendingOrderLine {
  purchaseOrderItemId: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  orderedQuantity: number;
  receivedQuantity: number;
  pendingQuantity: number;
  unitPrice: number;
}

export interface PendingOrder {
  id: string;
  orderNumber: string;
  status: string;
  expectedDate: string;
  vendorName: string | null;
  receivable: boolean;
  lines: PendingOrderLine[];
}

export interface GoodsReceiptInput {
  purchaseOrderId: string;
  warehouseId?: string;
  receiptDate?: string;
  vendorChallanNumber?: string | null;
  vendorInvoiceNumber?: string | null;
  vehicleNumber?: string | null;
  notes?: string | null;
  items: {
    purchaseOrderItemId: string;
    receivedQuantity: number;
    rejectedQuantity?: number;
    rejectionReason?: string | null;
    batchNumber?: string | null;
    heatNumber?: string | null;
    millTcNumber?: string | null;
  }[];
}

export interface GoodsReceiptQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'posted' | 'cancelled' | '';
  purchaseOrderId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export const goodsReceiptService = {
  getPage: async (params: GoodsReceiptQueryParams): Promise<Page<GoodsReceipt>> => (await api.get('/goods-receipts', { params: clean(params) })).data,
  getSummary: async (): Promise<GoodsReceiptSummary> => (await api.get('/goods-receipts/summary')).data,
  getReceivableOrders: async (search?: string): Promise<ReceivableOrder[]> =>
    (await api.get('/goods-receipts/receivable-orders', { params: clean({ search }) })).data,
  getPending: async (purchaseOrderId: string): Promise<PendingOrder> => (await api.get(`/goods-receipts/pending/${purchaseOrderId}`)).data,
  getOne: async (id: string): Promise<GoodsReceipt> => (await api.get(`/goods-receipts/${id}`)).data,
  create: async (data: GoodsReceiptInput): Promise<GoodsReceipt> => (await api.post('/goods-receipts', data)).data,
  cancel: async (id: string, reason: string): Promise<GoodsReceipt> => (await api.post(`/goods-receipts/${id}/cancel`, { reason })).data,
};

// ---- Delivery challans (dispatch)

export interface DeliveryChallanLine {
  id: string;
  salesOrderItemId: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  orderedQuantity: number;
  quantity: number;
  unitPrice: number;
  batchNumber: string | null;
  heatNumber: string | null;
  // The units that went, for a product kept by serial number
  serialNumbers?: string[];
}

export interface ChallanPackage {
  id: string;
  packageNumber: number;
  kind: string;
  netWeightKg: number | null;
  grossWeightKg: number | null;
  dimensions: string | null;
  notes: string | null;
  items: { id: string; deliveryChallanItemId: string; productName: string; sku: string; unit: string; batchNumber: string | null; quantity: number }[];
}

export interface GatePass {
  id: string;
  gatePassNumber: string;
  status: 'issued' | 'out';
  vehicleNumber: string;
  driverName: string | null;
  driverPhone: string | null;
  notes: string | null;
  issuedBy: string | null;
  createdAt: string;
  outAt: string | null;
  outBy: string | null;
}

export interface DeliveryChallan {
  id: string;
  challanNumber: string;
  dispatchDate: string;
  status: 'posted' | 'cancelled';
  salesOrderId: string;
  salesOrderNumber: string;
  customerName: string | null;
  warehouseName: string | null;
  transporter: string | null;
  vehicleNumber: string | null;
  lrNumber: string | null;
  shippingAddress?: string | null;
  packages?: number | null;
  notes?: string | null;
  cancelReason?: string | null;
  invoiceId: string | null;
  invoiceNumber?: string | null;
  itemCount: number;
  totalQuantity: number;
  totalValue: number;
  items?: DeliveryChallanLine[];
  // On one challan: its packages and its gate pass
  packing?: ChallanPackage[];
  gatePass?: GatePass | null;
  // On the list
  gatePassNumber?: string | null;
  gatePassStatus?: 'issued' | 'out' | null;
  createdAt: string;
}

export interface DispatchSummary {
  posted: number;
  cancelled: number;
  thisMonth: number;
  notInvoiced: number;
  ordersAwaitingDispatch: number;
  unitsAwaitingDispatch: number;
}

export interface DispatchableOrder {
  id: string;
  orderNumber: string;
  status: string;
  expectedDeliveryDate: string;
  customerName: string | null;
  orderedQuantity: number;
  dispatchedQuantity: number;
  pendingQuantity: number;
}

export interface PendingSalesLine {
  salesOrderItemId: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  stockOnHand: number;
  orderedQuantity: number;
  dispatchedQuantity: number;
  pendingQuantity: number;
  unitPrice: number;
  // Each unit has its own serial number; the ones that leave have to be named
  isSerialised?: boolean;
}

export interface PendingSalesOrder {
  id: string;
  orderNumber: string;
  status: string;
  customerName: string | null;
  shippingAddress: string | null;
  dispatchable: boolean;
  lines: PendingSalesLine[];
}

export interface DispatchInput {
  salesOrderId: string;
  warehouseId?: string;
  dispatchDate?: string;
  transporter?: string | null;
  vehicleNumber?: string | null;
  lrNumber?: string | null;
  shippingAddress?: string | null;
  packages?: number | null;
  notes?: string | null;
  items: { salesOrderItemId: string; quantity: number; batchNumber?: string | null; heatNumber?: string | null; batches?: { batchId: string; quantity: number }[]; serialNumbers?: string[] }[];
}

export interface DispatchQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'posted' | 'cancelled' | '';
  salesOrderId?: string;
}

export const dispatchService = {
  getPage: async (params: DispatchQueryParams): Promise<Page<DeliveryChallan>> => (await api.get('/dispatches', { params: clean(params) })).data,
  getSummary: async (): Promise<DispatchSummary> => (await api.get('/dispatches/summary')).data,
  getDispatchableOrders: async (): Promise<DispatchableOrder[]> => (await api.get('/dispatches/dispatchable-orders')).data,
  getPending: async (salesOrderId: string): Promise<PendingSalesOrder> => (await api.get(`/dispatches/pending/${salesOrderId}`)).data,
  getOne: async (id: string): Promise<DeliveryChallan> => (await api.get(`/dispatches/${id}`)).data,
  create: async (data: DispatchInput): Promise<DeliveryChallan> => (await api.post('/dispatches', data)).data,
  cancel: async (id: string, reason: string): Promise<DeliveryChallan> => (await api.post(`/dispatches/${id}/cancel`, { reason })).data,
  // Raises a draft tax invoice for the challan
  raiseInvoice: async (id: string): Promise<{ id: string; invoiceNumber: string; grandTotal: number }> =>
    (await api.post(`/gst/invoices/from-challan/${id}`)).data,
};
